import { PICKER_FILTER_ALL } from "../../shared/foodPicker/pickerBrowser";
import {
  NUTRITION_PLAN_INLINE_PICKER_KIND_EQUIVALENCE,
  normalizeNutritionPlanInlinePickerOwner,
} from "./nutritionPlanFoodPickerScopeUtils";

function normalizeId(value) {
  const normalizedValue = String(value ?? "").trim();
  return normalizedValue || null;
}

function shallowEqual(left, right) {
  if (left === right) {
    return true;
  }

  const leftKeys = Object.keys(left ?? {});
  const rightKeys = Object.keys(right ?? {});
  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((key) => left?.[key] === right?.[key]);
}

function createDefaultMealUiState(meal = null) {
  return {
    expanded: true,
    isMealNameMenuOpen: false,
    areNotesVisible: String(meal?.notes ?? "").trim().length > 0,
  };
}

function createDefaultOptionUiState() {
  return {
    searchText: "",
    sortKey: "default",
    pickerFilterKey: PICKER_FILTER_ALL,
    equivalenceDatabaseKey: PICKER_FILTER_ALL,
    equivalenceTargetId: null,
  };
}

function resolveNextState(currentState, updater, defaults) {
  const baseState = {
    ...defaults,
    ...(currentState ?? {}),
  };
  const nextState =
    typeof updater === "function"
      ? updater(baseState)
      : {
          ...baseState,
          ...(updater ?? {}),
        };

  return {
    ...defaults,
    ...(nextState ?? {}),
  };
}

export function createNutritionPlanEditorSession() {
  return {
    mealUiById: {},
    optionUiById: {},
  };
}

export function getNutritionPlanOptionSessionKey(mealId = null, optionId = null) {
  const normalizedMealId = normalizeId(mealId);
  const normalizedOptionId = normalizeId(optionId);
  if (!normalizedMealId || !normalizedOptionId) {
    return null;
  }

  return `${normalizedMealId}:${normalizedOptionId}`;
}

export function getNutritionPlanMealUiState(mealUiById = {}, meal = null) {
  const mealId = normalizeId(meal?.id);
  const defaults = createDefaultMealUiState(meal);
  if (!mealId) {
    return defaults;
  }

  return {
    ...defaults,
    ...(mealUiById?.[mealId] ?? {}),
  };
}

export function getNutritionPlanOptionUiState(
  optionUiById = {},
  mealId = null,
  optionId = null,
) {
  const defaults = createDefaultOptionUiState();
  const optionSessionKey = getNutritionPlanOptionSessionKey(mealId, optionId);
  if (!optionSessionKey) {
    return defaults;
  }

  return {
    ...defaults,
    ...(optionUiById?.[optionSessionKey] ?? {}),
  };
}

export function updateNutritionPlanMealUiState(session, meal = null, updater) {
  const currentSession = session ?? createNutritionPlanEditorSession();
  const mealId = normalizeId(typeof meal === "string" ? meal : meal?.id);
  if (!mealId) {
    return currentSession;
  }

  const currentMealUiById = currentSession.mealUiById ?? {};
  const currentState = currentMealUiById[mealId];
  const nextState = resolveNextState(
    currentState,
    updater,
    createDefaultMealUiState(typeof meal === "object" ? meal : null),
  );
  const comparableCurrentState = {
    ...createDefaultMealUiState(typeof meal === "object" ? meal : null),
    ...(currentState ?? {}),
  };
  if (shallowEqual(comparableCurrentState, nextState)) {
    return currentSession;
  }

  return {
    mealUiById: {
      ...currentMealUiById,
      [mealId]: nextState,
    },
    optionUiById: currentSession.optionUiById ?? {},
  };
}

export function updateNutritionPlanOptionUiState(
  session,
  { mealId = null, optionId = null } = {},
  updater,
) {
  const currentSession = session ?? createNutritionPlanEditorSession();
  const optionSessionKey = getNutritionPlanOptionSessionKey(mealId, optionId);
  if (!optionSessionKey) {
    return currentSession;
  }

  const currentOptionUiById = currentSession.optionUiById ?? {};
  const currentState = currentOptionUiById[optionSessionKey];
  const nextState = resolveNextState(
    currentState,
    updater,
    createDefaultOptionUiState(),
  );
  const comparableCurrentState = {
    ...createDefaultOptionUiState(),
    ...(currentState ?? {}),
  };
  if (shallowEqual(comparableCurrentState, nextState)) {
    return currentSession;
  }

  return {
    mealUiById: currentSession.mealUiById ?? {},
    optionUiById: {
      ...currentOptionUiById,
      [optionSessionKey]: nextState,
    },
  };
}

export function syncNutritionPlanEquivalentPickerSession(
  session,
  inlinePickerOwner = null,
) {
  const currentSession = session ?? createNutritionPlanEditorSession();
  const currentOptionUiById = currentSession.optionUiById ?? {};
  const normalizedInlinePickerOwner =
    normalizeNutritionPlanInlinePickerOwner(inlinePickerOwner);
  const shouldPreserveEquivalenceTarget =
    normalizedInlinePickerOwner?.kind ===
    NUTRITION_PLAN_INLINE_PICKER_KIND_EQUIVALENCE;
  let didChange = false;
  const nextOptionUiById = {};

  Object.entries(currentOptionUiById).forEach(([optionSessionKey, optionUiState]) => {
    const currentEquivalenceTargetId = normalizeId(optionUiState?.equivalenceTargetId);
    if (!currentEquivalenceTargetId) {
      nextOptionUiById[optionSessionKey] = optionUiState;
      return;
    }

    const shouldKeepCurrentEquivalenceTarget =
      shouldPreserveEquivalenceTarget &&
      getNutritionPlanOptionSessionKey(
        normalizedInlinePickerOwner?.mealId,
        normalizedInlinePickerOwner?.optionId,
      ) === optionSessionKey &&
      normalizedInlinePickerOwner?.itemId === currentEquivalenceTargetId;

    if (shouldKeepCurrentEquivalenceTarget) {
      nextOptionUiById[optionSessionKey] = optionUiState;
      return;
    }

    didChange = true;
    nextOptionUiById[optionSessionKey] = {
      ...optionUiState,
      equivalenceTargetId: null,
    };
  });

  if (!didChange) {
    return currentSession;
  }

  return {
    mealUiById: currentSession.mealUiById ?? {},
    optionUiById: nextOptionUiById,
  };
}

export function pruneNutritionPlanEditorSession(session, meals = []) {
  const currentSession = session ?? createNutritionPlanEditorSession();
  const currentMealUiById = currentSession.mealUiById ?? {};
  const currentOptionUiById = currentSession.optionUiById ?? {};
  const nextMealUiById = {};
  const nextOptionUiById = {};

  (Array.isArray(meals) ? meals : []).forEach((meal) => {
    const mealId = normalizeId(meal?.id);
    if (!mealId) {
      return;
    }

    if (Object.prototype.hasOwnProperty.call(currentMealUiById, mealId)) {
      nextMealUiById[mealId] = currentMealUiById[mealId];
    }

    const optionItemsById = new Map(
      (Array.isArray(meal?.options) ? meal.options : []).map((option) => [
        normalizeId(option?.id),
        new Set(
          (Array.isArray(option?.items) ? option.items : [])
            .map((item) => normalizeId(item?.id))
            .filter(Boolean),
        ),
      ]),
    );

    optionItemsById.forEach((itemIds, optionId) => {
      if (!optionId) {
        return;
      }

      const optionSessionKey = getNutritionPlanOptionSessionKey(mealId, optionId);
      if (!optionSessionKey) {
        return;
      }

      const currentOptionUi = currentOptionUiById[optionSessionKey];
      if (!currentOptionUi) {
        return;
      }

      const equivalenceTargetId = normalizeId(currentOptionUi?.equivalenceTargetId);
      nextOptionUiById[optionSessionKey] =
        equivalenceTargetId && !itemIds.has(equivalenceTargetId)
          ? {
              ...currentOptionUi,
              equivalenceTargetId: null,
            }
          : currentOptionUi;
    });
  });

  const currentMealKeys = Object.keys(currentMealUiById);
  const nextMealKeys = Object.keys(nextMealUiById);
  const currentOptionKeys = Object.keys(currentOptionUiById);
  const nextOptionKeys = Object.keys(nextOptionUiById);
  const didMealKeysChange =
    currentMealKeys.length !== nextMealKeys.length ||
    currentMealKeys.some((key) => currentMealUiById[key] !== nextMealUiById[key]);
  const didOptionKeysChange =
    currentOptionKeys.length !== nextOptionKeys.length ||
    currentOptionKeys.some((key) => currentOptionUiById[key] !== nextOptionUiById[key]);

  if (!didMealKeysChange && !didOptionKeysChange) {
    return currentSession;
  }

  return {
    mealUiById: nextMealUiById,
    optionUiById: nextOptionUiById,
  };
}
